/// <amd-module name="Test/TestPlansLibrary/LibraryHub/Utils/TestPlansLibraryUtils" />
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ILibraryWorkItemDataProviderState } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryContracts";
import { WorkItemConstants, LibraryQueryConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";

/**
 * Returns the data provider content present in JSON island
 */
export function getDataProviderContent(pageContext: IVssPageContext): Contracts.LibraryWorkItemsData {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const data = contributionService.getData<Contracts.LibraryWorkItemsData>(
        "ms.vss-test-web.test-plans-library-data-provider"
    ) as Contracts.LibraryWorkItemsData;
    return data;
}

/**
 * Returns the data provider content in an async manner
 */
export async function getDataProviderContentAsync(
    pageContext: IVssPageContext,
    dataProviderContext: { [key: string]: any }
): Promise<Contracts.LibraryWorkItemsData | undefined> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const data = contributionService.getDataAsync<Contracts.LibraryWorkItemsData>(
        "ms.vss-test-web.test-plans-library-data-provider",
        dataProviderContext
    );
    return data;
}

/**
 * Returns the data provider context that is held in state
 * @param libraryWorkItemsData The data returned by data provider
 * @param queryType The type of query currently selected
 */
export function createWorkItemDataProviderState(
    libraryWorkItemsData: Contracts.LibraryWorkItemsData,
    queryType: string
): ILibraryWorkItemDataProviderState {
    return {
        queryType: queryType,
        continuationToken: libraryWorkItemsData.continuationToken,
        hasMoreElements: libraryWorkItemsData.hasMoreElements,
        columnOptions: libraryWorkItemsData.columnOptions
    };
}

/**
 * Given the data provider context, returns whether there are more elements pending to be fetched or not
 * @param libraryWorkItemsData The data provider context held in state
 */
export function hasMoreTestCases(libraryWorkItemsData: ILibraryWorkItemDataProviderState | undefined): boolean {
    if (!libraryWorkItemsData) {
        return false;
    }

    return libraryWorkItemsData.hasMoreElements;
}

/**
 * Given the data provider context, it returns with a dictionary that can be passed to data provider during the next call
 * @param dataProviderState The data provider context held in state
 */
export function getDataProviderContextContract(dataProviderState: ILibraryWorkItemDataProviderState): Contracts.LibraryWorkItemsDataProviderRequest {
    let providerContext: Contracts.LibraryWorkItemsDataProviderRequest = {
        columnOptions: dataProviderState.columnOptions,
        libraryQueryType: getLibraryQuery(dataProviderState.queryType),
        filterValues: dataProviderState.filterValues,
        continuationToken: dataProviderState.continuationToken,
        orderByField: WorkItemConstants.WitIdField
    } as Contracts.LibraryWorkItemsDataProviderRequest;

    return providerContext;
}

/**
 * Translates internal queryType key into data provider's contract
 * @param queryType A valid queryType from the class of `LibraryQueryConstants`
 */
function getLibraryQuery(queryType: string): Contracts.TestPlansLibraryQuery {
    let libraryQuery: Contracts.TestPlansLibraryQuery = Contracts.TestPlansLibraryQuery.AllTestCases;

    switch (queryType) {
        case LibraryQueryConstants.QueryAllTestCasesKey:
            libraryQuery = Contracts.TestPlansLibraryQuery.AllTestCases;
            break;
        case LibraryQueryConstants.QueryTestsWithActiveBugsKey:
            libraryQuery = Contracts.TestPlansLibraryQuery.TestCasesWithActiveBugs;
            break;
        case LibraryQueryConstants.QueryTestsWithoutRequirementsKey:
            libraryQuery = Contracts.TestPlansLibraryQuery.TestCasesNotLinkedToRequirements;
            break;
        case LibraryQueryConstants.QueryTestsWithRequirementsKey:
            libraryQuery = Contracts.TestPlansLibraryQuery.TestCasesLinkedToRequirements;
            break;
    }

    return libraryQuery;
}
